// DTO de "Líneas de producción" del directivo (rediseño 2026-07, pantalla 2).
//
//   - GET /api/directivo/lineas?desde&hasta
//
// Estado de TODAS las líneas activas del tenant sobre un rango, agrupables en
// el FE como Planta → Área → Línea (el filtro de planta y la búsqueda viven en
// cliente, igual que el Dashboard). Reglas de datos:
//   - Por LÍNEA solo existe cumplimiento vs CAPACIDAD (WS-4): el programa del
//     cliente y su cumplimiento vienen por ÁREA (nivel nativo de la
//     reconciliación) en el bloque `areas`.
//   - Tiendas (es_tienda): cadencia libre sin meta — el FE aplica la regla
//     interina meta = producido (100%*). Su plata incluye insumos COBRADOS.
//   - $ null sin puede_ver_costos.
//   - DN10: el $ por línea va sobre producción VALIDADA; lo capturado sin
//     validar se declara en `pendiente_validacion` y no suma.
import { z } from 'zod';
import { pendienteValidacionUtilidadSchema } from './coordinador_cumplimiento.js';
import { conectividadLecturaSchema } from './ingesta_maquina.js';
export const directivoLineasQuerySchema = z
    .object({
    desde: z.string().date(),
    hasta: z.string().date(),
    /** Con turno, el programa de área NO aplica (es diario, sin turno). */
    turno: z.coerce.number().int().min(1).max(3).optional(),
})
    .refine((q) => q.desde <= q.hasta, { message: 'desde debe ser <= hasta' });
/** Fila por línea activa (incluye tiendas; aparece aunque no tenga capturas). */
export const lineaEstadoSchema = z.object({
    linea_id: z.string().uuid(),
    codigo: z.string(),
    planta_id: z.string().uuid(),
    area_id: z.string().uuid().nullable(),
    es_tienda: z.boolean(),
    unidades: z.number(),
    meta_capacidad_unds: z.number(),
    horas_trabajadas: z.number(),
    paros_min: z.number(),
    paros_eventos: z.number(),
    paro_activo: z.boolean(),
    skus_sin_precio: z.number(),
    // ── $ (null si !puede_ver_costos) ──
    valor_cop: z.number().nullable(),
    ingreso_insumos_cop: z.number().nullable(),
    costo_mo_cop: z.number().nullable(),
    /** DN10: capturado sin validar de la línea (valor incluye insumos) — declarado, NO sumado. */
    pendiente_validacion: pendienteValidacionUtilidadSchema.nullable().optional(),
    /** Ingesta de máquina (opcional): null en líneas manuales. */
    ...conectividadLecturaSchema.shape,
});
/** Subtotal por área: el nivel donde el programa cliente SÍ es real. */
export const areaProgramaSchema = z.object({
    area_id: z.string().uuid(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
    programa_unds: z.number(),
    real_planif_unds: z.number(),
    fuera_plan_unds: z.number(),
});
export const paretoCausalSchema = z.object({
    causal: z.string(),
    color: z.string().nullable(),
    minutos: z.number(),
    eventos: z.number(),
    unds_perdidas: z.number(),
    valor_cop: z.number().nullable(),
});
export const distribucionParosSchema = z.object({
    /** Minutos de paro por turno (1..3) — por planta para filtrar en cliente. */
    por_turno: z.array(z.object({ planta_id: z.string().uuid(), turno: z.number().int(), minutos: z.number() })),
    /** Minutos de paro por hora del día ('00'..'23') — por planta. */
    por_hora: z.array(z.object({ planta_id: z.string().uuid(), hora: z.string(), minutos: z.number() })),
});
/** Pareto por causal viene POR PLANTA para que el filtro de planta no refetchee. */
export const paretoCausalPlantaSchema = paretoCausalSchema.extend({
    planta_id: z.string().uuid(),
});
export const directivoLineasSchema = z.object({
    rango: z.object({ desde: z.string().date(), hasta: z.string().date() }),
    turno: z.number().int().nullable(),
    puede_ver_costos: z.boolean(),
    plantas: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), codigo: z.string() })),
    lineas: z.array(lineaEstadoSchema),
    areas: z.array(areaProgramaSchema),
    pareto: z.array(paretoCausalPlantaSchema),
    distribucion: distribucionParosSchema,
    /** DN1: paros ABIERTOS del rango (eventos distintos) — aparte, no suman. */
    paros_en_curso: z.number().int().min(0),
    /** DN10: base del $ de todas las líneas. */
    base_utilidad: z.literal('validada').optional(),
    /** DN10: pendiente de validar del rango, suma de todas las líneas. null sin costos. */
    pendiente_validacion_utilidad: pendienteValidacionUtilidadSchema.nullable().optional(),
    actualizado_at: z.string(),
});
//# sourceMappingURL=directivo_lineas.js.map